import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Alert, Badge, Button, Col, Form as UiForm, InputGroup, Row, Spinner } from '@/components/ui';
import { Form, formService, PaginatedFormsResponse, FormQueryParams, FormFilterOptions, FormFilterCounts, DuplicateFormWarning } from '../../../services/formService';

import FormCard from './FormCard';
import { Task } from '../../../types/Task';
import { taskService } from '../../../services/taskService';
import effortService from '../../../services/effortService';
import type {
  FormLabourOvertimeAllocationRow,
  PersistedLabourEffort
} from '../../../utils/labourOvertimeDisplay';
import FormFilters, { FormAdvancedFilters } from './FormFilters';
import {
  APPROVAL_FORM_TYPES,
  type ApprovalFormType,
  type ApprovalStatusFilter,
} from '../config/approvalFormTypes';
import ServerPagination from '../../../components/ServerPagination';
import { useAuth } from '../../../contexts/AuthContext';
import Toast, { ToastType } from '../../../components/Toast';
import DuplicateFormWarningModal from '../../../components/DuplicateFormWarningModal';
import {
  formStatusToSessionStatus,
  updateSessionSubmissionStatus,
} from '../../hired-equipment/utils/sessionSubmissions';
import '../styles/FormList.css';

interface FormListProps {
  formType: ApprovalFormType;
  onFormTypeChange?: (formType: ApprovalFormType) => void;
  isSearchVisible?: boolean;
  onToggleSearch?: () => void;
  hasWorkId?: boolean;
}

// Add pagination constant - removed ITEMS_PER_PAGE since we'll use server pagination

const VALID_STATUSES = ['all', 'pending', 'need revisions', 'approved', 'declined'] as const;

const STATUS_FILTER_BUTTONS: { value: ApprovalStatusFilter; label: string }[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'need revisions', label: 'Need Revisions' },
  { value: 'approved', label: 'Approved' },
  { value: 'declined', label: 'Declined' },
];

const EMPTY_ADVANCED_FILTERS: FormAdvancedFilters = {
  employeeNames: [],
  subcontractorNames: [],
  startDate: null,
  endDate: null,
  foremanAreaIds: [],
  serviceAreaIds: [],
};

/**
 * URL params carry multi-value filters as a comma separated list (same convention as the
 * server and the Work IDs page). A single bare value still works.
 */
function csvToList(raw: string | null | undefined): string[] {
  return (raw ?? '')
    .split(',')
    .map((value) => value.trim())
    .filter((value) => value.length > 0);
}

const sameList = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((value, index) => value === b[index]);

function getInitialFiltersFromSearchParams(searchParams: URLSearchParams) {
  const status = searchParams.get('status');
  const foremanArea = searchParams.get('foremanArea') ?? searchParams.get('foremanAreaId');
  const serviceAreaId = searchParams.get('serviceAreaId');
  return {
    statusFilter: (status && VALID_STATUSES.includes(status as typeof VALID_STATUSES[number])) ? status : 'pending',
    foremanAreaIds: csvToList(foremanArea),
    serviceAreaIds: csvToList(serviceAreaId),
  };
}

const FormList: React.FC<FormListProps> = ({ formType, onFormTypeChange, isSearchVisible, onToggleSearch }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const [forms, setForms] = useState<Form[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<string>(() =>
    getInitialFiltersFromSearchParams(searchParams).statusFilter
  );
  const [isLoading, setIsLoading] = useState(false);
  const [availableTasks, setAvailableTasks] = useState<Task[]>([]);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [persistedLabourByFormId, setPersistedLabourByFormId] = useState<
    Record<string, PersistedLabourEffort[]>
  >({});
  const [labourOtAllocationsByFormId, setLabourOtAllocationsByFormId] = useState<
    Record<string, FormLabourOvertimeAllocationRow[]>
  >({});
  const [advancedFilters, setAdvancedFilters] = useState<FormAdvancedFilters>(() => {
    const { foremanAreaIds, serviceAreaIds } = getInitialFiltersFromSearchParams(searchParams);
    return {
      ...EMPTY_ADVANCED_FILTERS,
      foremanAreaIds,
      serviceAreaIds,
    };
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Add pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    totalCount: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false
  });

  // Add filter options state for server-side filtering
  const [filterOptions, setFilterOptions] = useState<FormFilterOptions>({
    serviceAreas: [],
    foremanAreas: [],
    statuses: [],
    employeeNames: [],
    subcontractorNames: []
  });

  // Add filter counts state for badges
  const [filterCounts, setFilterCounts] = useState<FormFilterCounts>({
    statusCounts: {
      all: 0,
      pending: 0,
      approved: 0,
      'need revisions': 0,
      declined: 0,
      'revisions completed': 0
    },
    formTypeCounts: {
      all: 0,
      'Daily Log': 0,
      'Hired Equipment': 0
    }
  });

  // Toast state
  const [toast, setToast] = useState<{
    show: boolean;
    message: string;
    type: ToastType;
  }>({
    show: false,
    message: '',
    type: 'info'
  });

  // Duplicate warning modal state
  const [duplicateWarningModal, setDuplicateWarningModal] = useState<{
    isOpen: boolean;
    duplicateWarning: DuplicateFormWarning | null;
    pendingFormId: string | null;
    /** HE-F21: the vendor-PDF answer must survive the duplicate detour and the retry. */
    pendingOptions?: { emailVendorPdf?: boolean; vendorEmail?: { to: string; subject: string; bodyText: string } };
  }>({
    isOpen: false,
    duplicateWarning: null,
    pendingFormId: null
  });

  // Track forms being processed (formId -> action type)
  const [processingForms, setProcessingForms] = useState<Map<string, string>>(new Map());
  const [fadingForms, setFadingForms] = useState<Map<string, string>>(new Map());

  // Form to expand when opened via deep link (e.g. /approvals?formId=123 from Task efforts)
  const formIdFromUrl = searchParams.get('formId');
  const [expandFormId, setExpandFormId] = useState<string | null>(() => formIdFromUrl);

  const filterCountsRequestIdRef = useRef(0);
  const formsFetchRequestIdRef = useRef(0);

  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status);
    setPagination((prev) => ({ ...prev, page: 1 }));
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (status === 'pending') {
          next.delete('status');
        } else {
          next.set('status', status);
        }
        return next;
      },
      { replace: true },
    );
  };

  // Sync URL params (status + foreman area + service area) when they change (e.g. back/forward or link).
  // Initial values are set from URL in useState above so the first fetch already has correct filters.
  useEffect(() => {
    const status = searchParams.get('status');
    const foremanArea = searchParams.get('foremanArea');
    const serviceAreaId = searchParams.get('serviceAreaId');
    const formId = searchParams.get('formId');
    if (status && VALID_STATUSES.includes(status as typeof VALID_STATUSES[number])) {
      setStatusFilter(status);
    }
    if (foremanArea !== null && foremanArea !== undefined || serviceAreaId !== null && serviceAreaId !== undefined) {
      setAdvancedFilters((prev) => {
        const nextForemanAreaIds = foremanArea !== null && foremanArea !== undefined
          ? csvToList(foremanArea)
          : prev.foremanAreaIds;
        const nextServiceAreaIds = serviceAreaId !== null && serviceAreaId !== undefined
          ? csvToList(serviceAreaId)
          : prev.serviceAreaIds;
        // Keep the same object identity when nothing changed: advancedFilters is a fetch dependency.
        if (sameList(nextForemanAreaIds, prev.foremanAreaIds) && sameList(nextServiceAreaIds, prev.serviceAreaIds)) {
          return prev;
        }
        return { ...prev, foremanAreaIds: nextForemanAreaIds, serviceAreaIds: nextServiceAreaIds };
      });
    }
    if (formId !== null && formId !== undefined) {
      setExpandFormId(formId);
    }
  }, [searchParams]);

  const getFormCountByStatus = (status: string) => {
    return filterCounts.statusCounts[status as keyof typeof filterCounts.statusCounts] || 0;
  };

  /**
   * Multi-value filters travel as comma separated lists (`foremanArea=12,34`).
   * Empty selections omit the param entirely, which the server reads as "no filter".
   * Service areas are still sent by NAME (server matches on name), ids are resolved here.
   *
   * Employee / subcontractor names are FREE TEXT and can legitimately contain commas
   * ("Acme Ltd., Inc"), so each element is percent-encoded before joining (commas -> %2C)
   * and the server decodes after splitting. Same comma-safe token convention as the
   * Work IDs work-source filter.
   */
  const buildAdvancedFilterQueryParams = (): Pick<
    FormQueryParams,
    'serviceArea' | 'foremanArea' | 'employeeName' | 'subcontractorName'
  > => {
    const serviceArea = advancedFilters.serviceAreaIds
      .map((id) => filterOptions.serviceAreas.find((sa) => sa.id.toString() === id)?.name)
      .filter((name): name is string => Boolean(name))
      .join(',');

    const namesToCsv = (names: string[]): string => names.map(encodeURIComponent).join(',');

    return {
      ...(serviceArea && { serviceArea }),
      ...(advancedFilters.foremanAreaIds.length > 0 && {
        foremanArea: advancedFilters.foremanAreaIds.join(',')
      }),
      ...(advancedFilters.employeeNames.length > 0 && {
        employeeName: namesToCsv(advancedFilters.employeeNames)
      }),
      ...(advancedFilters.subcontractorNames.length > 0 && {
        subcontractorName: namesToCsv(advancedFilters.subcontractorNames)
      })
    };
  };

  const buildCountsQueryParams = (): Omit<FormQueryParams, 'page' | 'limit' | 'status'> => ({
    ...(formType !== 'all' && { formType }),
    ...buildAdvancedFilterQueryParams(),
    ...(searchTerm && { search: searchTerm }),
    ...(advancedFilters.startDate && { dateFrom: advancedFilters.startDate }),
    ...(advancedFilters.endDate && { dateTo: advancedFilters.endDate }),
  });

  // Updated fetch functions
  const fetchForms = async (
    resetPage: boolean = false,
    signal?: AbortSignal,
    /** Extra task IDs to load (e.g. from just-approved form) so efforts/OT merge even if list page shifts. */
    mergeTaskIds: number[] = [],
    requestId?: number,
  ) => {
    const isLatest = () => requestId == null || requestId === formsFetchRequestIdRef.current;
    try {
      setLoading(true);
      setError(null);

      // Use current pagination page or reset to 1
      const currentPage = resetPage ? 1 : pagination.page;

      // Build query parameters
      const queryParams: FormQueryParams = {
        page: currentPage,
        limit: pagination.limit,
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(formType !== 'all' && { formType: formType }),
        ...buildAdvancedFilterQueryParams(),
        ...(searchTerm && { search: searchTerm }),
        ...(advancedFilters.startDate && { dateFrom: advancedFilters.startDate }),
        ...(advancedFilters.endDate && { dateTo: advancedFilters.endDate })
      };

      console.log('Sending query params:', queryParams); // Debug log

      const response = await formService.getFormsPaginated(queryParams, signal);

      if (signal?.aborted || !isLatest()) return;

      // No need for client-side filtering anymore since we're filtering on the server
      setForms(response.data);
      setPagination({
        page: response.pagination.page,
        limit: response.pagination.limit,
        totalCount: response.pagination.totalCount,
        totalPages: response.pagination.totalPages,
        hasNextPage: response.pagination.hasNextPage,
        hasPreviousPage: response.pagination.hasPreviousPage
      });

      if (resetPage) {
        setPagination(prev => ({ ...prev, page: 1 }));
      }

      await fetchTasksForForms(response.data, mergeTaskIds);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      if (!isLatest()) return;
      console.error('Error loading forms:', error);
      setError('Failed to load forms. Please try again later.');
      setForms([]);
      setPagination(prev => ({ ...prev, totalCount: 0, totalPages: 0 }));
    } finally {
      if (isLatest()) {
        setLoading(false);
      }
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const options = await formService.getFilterOptions();
      setFilterOptions(options);
      console.log('Fetched filter options:', options); // Debug log
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  // Helper function to extract task IDs from forms
  const extractTaskIdsFromForms = (forms: Form[]): number[] => {
    const taskIds = new Set<number>();
    
    forms.forEach(form => {
      form.fields.forEach(field => {
        // Look for Task fields within WorkCompleted sections
        if (field.path.includes('WorkCompleted') && field.field === 'Task' && field.path.endsWith('.Task')) {
          const taskId = parseInt(field.value);
          if (!isNaN(taskId) && taskId > 0) {
            taskIds.add(taskId);
            console.log(`Found task ID from Task field: ${taskId} in form ${form.id}`);
          }
        }
        
        // Also check WorkCompletedID fields
        if (field.path.includes('WorkCompleted') && field.field === 'WorkCompletedID') {
          const taskId = parseInt(field.value);
          if (!isNaN(taskId) && taskId > 0) {
            taskIds.add(taskId);
            console.log(`Found task ID from WorkCompletedID field: ${taskId} in form ${form.id}`);
          }
        }

        // TaskId (numeric id) — matches FormSummaryView; needed so /light/by-ids loads task.efforts for OT merge
        if (
          field.path.includes('WorkCompleted') &&
          (field.field === 'TaskId' || /\.TaskId$/i.test(field.path))
        ) {
          const taskId = parseInt(String(field.value), 10);
          if (!isNaN(taskId) && taskId > 0) {
            taskIds.add(taskId);
            console.log(`Found task ID from TaskId field: ${taskId} in form ${form.id}`);
          }
        }
      });
    });
    
    const result = Array.from(taskIds);
    console.log(`Extracted ${result.length} unique task IDs from ${forms.length} forms:`, result);
    return result;
  };

  /** Batched labour rows (clone tasks + DRM OT) for submission summary merge. */
  const fetchPersistedLabourForForms = async (forms: Form[]) => {
    const ids = Array.from(new Set(forms.map((f) => String(f.id)).filter(Boolean))).slice(0, 100);
    if (ids.length === 0) {
      setPersistedLabourByFormId({});
      setLabourOtAllocationsByFormId({});
      return;
    }
    try {
      const [rows, allocRows] = await Promise.all([
        effortService.getLabourEffortsByFormIds(ids),
        effortService.getLabourOvertimeAllocationsByFormIds(ids)
      ]);
      const byForm: Record<string, PersistedLabourEffort[]> = {};
      for (const row of rows) {
        const fid = String(row.formId ?? '');
        if (!byForm[fid]) byForm[fid] = [];
        byForm[fid].push(row);
      }
      const byFormAlloc: Record<string, FormLabourOvertimeAllocationRow[]> = {};
      for (const row of allocRows) {
        const fid = String(row.formId ?? '');
        if (!byFormAlloc[fid]) byFormAlloc[fid] = [];
        byFormAlloc[fid].push(row);
      }
      setPersistedLabourByFormId(byForm);
      setLabourOtAllocationsByFormId(byFormAlloc);
    } catch (e) {
      console.error('Error fetching labour efforts for forms:', e);
      setPersistedLabourByFormId({});
      setLabourOtAllocationsByFormId({});
    }
  };

  /**
   * After labour OT edits: refetch allocation rows + labour efforts, and refresh light tasks
   * so `task.efforts` matches DB (Print PDF uses `availableTasks`, not the merged summary state).
   */
  const refreshLabourAndLightTasksForForms = async (forms: Form[]) => {
    const taskIdList = extractTaskIdsFromForms(forms);
    try {
      await Promise.all([
        fetchPersistedLabourForForms(forms),
        (async () => {
          if (taskIdList.length === 0) return;
          const tasks = await taskService.getLightTasksByIds(taskIdList);
          setAvailableTasks(tasks);
        })()
      ]);
    } catch (e) {
      console.error('Error refreshing labour/tasks after OT update:', e);
    }
  };

  // Fetch tasks referenced on the page + persisted labour by form (for OT on clone tasks).
  const fetchTasksForForms = async (forms: Form[], mergeTaskIds: number[] = []) => {
    try {
      setTasksLoading(true);
      const labourPromise = fetchPersistedLabourForForms(forms);

      const taskIds = new Set<number>(extractTaskIdsFromForms(forms));
      for (const id of mergeTaskIds) {
        if (typeof id === 'number' && !isNaN(id) && id > 0) {
          taskIds.add(id);
        }
      }
      const taskIdList = Array.from(taskIds);
      console.log('Extracted task IDs from forms (+ merge):', taskIdList);

      let tasksPromise: Promise<void> = Promise.resolve();
      if (taskIdList.length === 0) {
        setAvailableTasks([]);
      } else {
        tasksPromise = taskService
          .getLightTasksByIds(taskIdList)
          .then((tasks) => setAvailableTasks(tasks))
          .catch((err) => {
            console.error('Error fetching tasks for forms:', err);
            setAvailableTasks([]);
          });
      }

      await Promise.all([tasksPromise, labourPromise]);
    } catch (error) {
      console.error('Error fetching tasks/labour for forms:', error);
      setAvailableTasks([]);
    } finally {
      setTasksLoading(false);
    }
  };

  // Add pagination handlers
  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize: number) => {
    // Add a small delay to prevent immediate re-render from interfering with dropdown
    setTimeout(() => {
      setPagination(prev => ({ 
        ...prev, 
        limit: newPageSize, 
        page: 1 // Reset to first page when changing page size
      }));
    }, 100);
  };

  // Request id so we only apply the latest filter-counts response (avoid stale overwrites)
  const fetchFilterCounts = async () => {
    const requestId = ++filterCountsRequestIdRef.current;
    try {
      const counts = await formService.getFilterCounts(buildCountsQueryParams());
      // Only update state if this is still the latest request (ignore stale responses)
      if (requestId === filterCountsRequestIdRef.current) {
        setFilterCounts(counts);
      }
    } catch (error) {
      if (requestId === filterCountsRequestIdRef.current) {
        console.error('Error fetching filter counts:', error);
      }
    }
  };

  // Single effect to fetch forms when any relevant parameter changes.
  // Skip when formId is in URL (single-form view) so we don't load the full list.
  const fetchAbortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    if (formIdFromUrl) return; // Single-form view: handled by separate effect
    fetchAbortRef.current?.abort();
    const controller = new AbortController();
    fetchAbortRef.current = controller;
    const requestId = ++formsFetchRequestIdRef.current;
    fetchForms(false, controller.signal, [], requestId);
    return () => {
      controller.abort();
      fetchAbortRef.current = null;
    };
  }, [pagination.page, pagination.limit, statusFilter, formType, advancedFilters, searchTerm, filterOptions, formIdFromUrl]);

  // Status pill counts follow the same filters as the list (type tab, card filters, search).
  useEffect(() => {
    if (formIdFromUrl) return;
    fetchFilterCounts();
  }, [formType, advancedFilters, searchTerm, filterOptions, formIdFromUrl]);

  // Initial data loading
  useEffect(() => {
    fetchFilterOptions();
  }, []);

  // Pre-populate filters from URL (e.g. from homepage widget link) so dropdowns show selected items
  useEffect(() => {
    const foremanAreaId = searchParams.get('foremanAreaId');
    const serviceAreaId = searchParams.get('serviceAreaId');
    if (foremanAreaId != null || serviceAreaId != null) {
      setAdvancedFilters((prev) => {
        const nextForemanAreaIds = foremanAreaId != null && foremanAreaId !== ''
          ? csvToList(foremanAreaId)
          : prev.foremanAreaIds;
        const nextServiceAreaIds = serviceAreaId != null && serviceAreaId !== ''
          ? csvToList(serviceAreaId)
          : prev.serviceAreaIds;
        if (sameList(nextForemanAreaIds, prev.foremanAreaIds) && sameList(nextServiceAreaIds, prev.serviceAreaIds)) {
          return prev;
        }
        return { ...prev, foremanAreaIds: nextForemanAreaIds, serviceAreaIds: nextServiceAreaIds };
      });
    }
  }, [searchParams]);

  // When opened via deep link (?formId=), show only that single form (no list, no extra timecards)
  useEffect(() => {
    if (!formIdFromUrl) return;
    setLoading(true);
    setForms([]);
    let cancelled = false;
    formService.getForm(formIdFromUrl)
      .then((form) => {
        if (cancelled) return;
        setForms([form]);
        setExpandFormId(formIdFromUrl);
        fetchTasksForForms([form]);
      })
      .catch((err) => {
        if (!cancelled) {
          console.warn('Could not load form by id for deep link:', formIdFromUrl, err);
          setForms([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [formIdFromUrl]);

  // Helper function to get date value from form field
  const getDateFromForm = (form: Form, fieldName: string): string | null => {
    const dateField = form.fields.find(
      field => field.field === fieldName || 
              field.question.toLowerCase().includes(fieldName.toLowerCase())
    );
    
    if (dateField?.value) {
      return dateField.value;
    }
    
    return null;
  };

  // Helper function to get employee name from form
  const getEmployeeNameFromForm = (form: Form): string | null => {
    const employeeField = form.fields.find(
      field => field.field === 'EmployeeName' || 
              field.question.toLowerCase().includes('employee name')
    );
    
    return employeeField?.value || null;
  };

  // Helper function to toggle search visibility
  const toggleSearch = () => {
    if (onToggleSearch) {
      onToggleSearch();
    }
    if (isSearchVisible) {
      setSearchTerm(''); // Clear search when hiding
      setSearchInput(''); // Clear input when hiding
    }
  };

  const handleSearch = () => {
    setSearchTerm(searchInput.trim());
  };

  const handleSearchInputChange = (value: string) => {
    setSearchInput(value);
  };

  // Manual search only - no automatic search on typing

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
  };



  const handleApprove = async (
    formId: string,
    confirmDuplicate: boolean = false,
    options?: { emailVendorPdf?: boolean; vendorEmail?: { to: string; subject: string; bodyText: string } }
  ): Promise<void> => {
    // Find the form to check the employee name
    const form = forms.find(f => f.id === formId);
    if (form && currentUser) {
      const employeeName = getEmployeeNameFromForm(form);
      const currentUserFullName = `${currentUser.name} ${currentUser.lastName}`;
      if (employeeName === currentUserFullName) {
        setToast({
          show: true,
          message: "Can't approve own timecard",
          type: 'warning'
        });
        return;
      }
    }

    // Mark form as processing immediately
    setProcessingForms(prev => new Map(prev).set(formId, 'approve'));

    try {
      const approvedForm = await formService.approveForm(formId, confirmDuplicate, options);
      const mergeTaskIds = extractTaskIdsFromForms([approvedForm]);
      
      // Show task creation summary in testing environment
        if (process.env.NODE_ENV === 'development' && (approvedForm as any)?.processingResult) {
          const { createdTasks, errors } = (approvedForm as any).processingResult;
          
          if (createdTasks.length > 0) {
            const taskSummary = createdTasks.map((task: any) => {
              let summary = `${task.taskType} task ${task.taskNumber} for ${task.activityName || 'Unknown Activity'}${task.assetName ? ` (${task.assetName})` : ''}${task.contractArea ? ` [${task.contractArea}]` : ''}`;
              
              if (task.efforts && task.efforts.length > 0) {
                const effortSummary = task.efforts.map((effort: any) => 
                  `  - ${effort.type}: ${effort.quantity} ${effort.description} @ $${effort.rate} (${effort.employeeName})`
                ).join('\n');
                summary += `\n  Efforts:\n${effortSummary}`;
              }
              
              return summary;
            }).join('\n\n');
            
            const summaryMessage = `✅ Form approved successfully!\n\nProcessed ${createdTasks.length} task(s) (created new or allocated efforts to existing):\n\n${taskSummary}${errors.length > 0 ? `\n\n⚠️ Errors:\n${errors.join('\n')}` : ''}`;
            
            alert(summaryMessage);
          } else {
            alert('✅ Form approved successfully!\n\nNo tasks were created.');
          }
        }
      
      // Move from processing to fading state
      setProcessingForms(prev => {
        const next = new Map(prev);
        next.delete(formId);
        return next;
      });
      setFadingForms(prev => new Map(prev).set(formId, 'approve'));

      // Wait for fade animation to complete (~1 second)
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Refresh both the forms list and filter counts from server
      await Promise.all([
        fetchForms(false, undefined, mergeTaskIds),
        fetchFilterCounts()
      ]);

      // Clear fading state
      setFadingForms(prev => {
        const next = new Map(prev);
        next.delete(formId);
        return next;
      });

      setToast({
        show: true,
        message: 'Form approved successfully',
        type: 'success'
      });
    } catch (error: any) {
      console.error('Error approving form:', error);
      
      // Remove from processing state on error
      setProcessingForms(prev => {
        const next = new Map(prev);
        next.delete(formId);
        return next;
      });

      // Handle duplicate Daily Log warning
      if (error.response?.status === 409 && error.response?.data?.error === 'DUPLICATE_DAILY_LOG_WARNING') {
        const duplicateWarning: DuplicateFormWarning = error.response.data;
        
        // Show the duplicate warning modal instead of window.confirm
        setDuplicateWarningModal({
          isOpen: true,
          duplicateWarning,
          pendingFormId: formId,
          pendingOptions: options
        });
        return;
      }
      
      // Extract error message from server response
      const errorMessage = error.response?.data?.message 
        || error.response?.data?.error 
        || error.message 
        || 'Failed to approve form';
      
      setToast({
        show: true,
        message: errorMessage,
        type: 'error'
      });
    }
  };

  // Handle duplicate warning modal actions
  const handleDuplicateWarningConfirm = () => {
    // Close modal and proceed with approval
    const formId = duplicateWarningModal.pendingFormId;
    const pendingOptions = duplicateWarningModal.pendingOptions;
    setDuplicateWarningModal({
      isOpen: false,
      duplicateWarning: null,
      pendingFormId: null,
      pendingOptions: undefined
    });
    
    if (formId) {
      // Retry approval with confirmDuplicate = true, keeping the vendor-PDF answer.
      handleApprove(formId, true, pendingOptions);
    }
  };

  const handleDuplicateWarningCancel = () => {
    // Just close the modal without approving
    setDuplicateWarningModal({
      isOpen: false,
      duplicateWarning: null,
      pendingFormId: null
    });
  };

  const handleReject = async (formId: string, comment: string) => {
    // Mark form as processing immediately
    setProcessingForms(prev => new Map(prev).set(formId, 'reject'));

    try {
      await formService.rejectForm(formId, comment);
      updateSessionSubmissionStatus(formId, formStatusToSessionStatus('need revisions'));
      
      // Move from processing to fading state
      setProcessingForms(prev => {
        const next = new Map(prev);
        next.delete(formId);
        return next;
      });
      setFadingForms(prev => new Map(prev).set(formId, 'reject'));

      // Wait for fade animation to complete (~1 second)
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Refresh both the forms list and filter counts from server
      await Promise.all([
        fetchForms(),
        fetchFilterCounts()
      ]);

      // Clear fading state
      setFadingForms(prev => {
        const next = new Map(prev);
        next.delete(formId);
        return next;
      });

      setToast({
        show: true,
        message: 'Sent for revisions',
        type: 'success'
      });
    } catch (error) {
      console.error('Error rejecting form:', error);
      
      // Remove from processing state on error
      setProcessingForms(prev => {
        const next = new Map(prev);
        next.delete(formId);
        return next;
      });

      const message =
        error instanceof Error ? error.message : 'Failed to reject form';

      setToast({
        show: true,
        message,
        type: 'error'
      });
    }
  };

  const handleDecline = async (formId: string, comment: string) => {
    // Mark form as processing immediately
    setProcessingForms(prev => new Map(prev).set(formId, 'decline'));

    try {
      await formService.declineForm(formId, comment);
      updateSessionSubmissionStatus(formId, formStatusToSessionStatus('declined'));
      
      // Move from processing to fading state
      setProcessingForms(prev => {
        const next = new Map(prev);
        next.delete(formId);
        return next;
      });
      setFadingForms(prev => new Map(prev).set(formId, 'decline'));

      // Wait for fade animation to complete (~1 second)
      await new Promise(resolve => setTimeout(resolve, 1000));

      await Promise.all([
        fetchForms(),
        fetchFilterCounts()
      ]);

      // Clear fading state
      setFadingForms(prev => {
        const next = new Map(prev);
        next.delete(formId);
        return next;
      });

      setToast({
        show: true,
        message: 'Form declined successfully',
        type: 'success'
      });
    } catch (error) {
      console.error('Error declining form:', error);
      
      // Remove from processing state on error
      setProcessingForms(prev => {
        const next = new Map(prev);
        next.delete(formId);
        return next;
      });

      setToast({
        show: true,
        message: 'Failed to decline form',
        type: 'error'
      });
    }
  };

  const handleWithdrawApproval = async (formId: string) => {
    // Mark form as processing immediately
    setProcessingForms(prev => new Map(prev).set(formId, 'withdrawApproval'));

    try {
      await formService.withdrawApproval(formId);
      updateSessionSubmissionStatus(formId, 'pending');
      
      // Move from processing to fading state
      setProcessingForms(prev => {
        const next = new Map(prev);
        next.delete(formId);
        return next;
      });
      setFadingForms(prev => new Map(prev).set(formId, 'withdrawApproval'));

      // Wait for fade animation to complete (~1 second)
      await new Promise(resolve => setTimeout(resolve, 1000));

      await Promise.all([
        fetchForms(),
        fetchFilterCounts()
      ]);

      // Clear fading state
      setFadingForms(prev => {
        const next = new Map(prev);
        next.delete(formId);
        return next;
      });

      setToast({
        show: true,
        message: 'Approval withdrawn successfully',
        type: 'success'
      });
    } catch (error) {
      console.error('Error withdrawing approval:', error);
      
      // Remove from processing state on error
      setProcessingForms(prev => {
        const next = new Map(prev);
        next.delete(formId);
        return next;
      });

      const message = error instanceof Error ? error.message : 'Failed to withdraw approval';
      const isWithdrawBlocked = typeof message === 'string' && message.toLowerCase().startsWith('cannot withdraw approval');
      setToast({
        show: true,
        message,
        type: isWithdrawBlocked ? 'warning' : 'error'
      });
    }
  };

  const handleWithdrawDecline = async (formId: string) => {
    setProcessingForms(prev => new Map(prev).set(formId, 'withdrawDecline'));

    try {
      await formService.withdrawDecline(formId);

      setProcessingForms(prev => {
        const next = new Map(prev);
        next.delete(formId);
        return next;
      });
      setFadingForms(prev => new Map(prev).set(formId, 'withdrawDecline'));

      await new Promise(resolve => setTimeout(resolve, 1000));

      await Promise.all([
        fetchForms(),
        fetchFilterCounts()
      ]);

      setFadingForms(prev => {
        const next = new Map(prev);
        next.delete(formId);
        return next;
      });

      setToast({
        show: true,
        message: 'Decline withdrawn successfully',
        type: 'success'
      });
    } catch (error) {
      console.error('Error withdrawing decline:', error);

      setProcessingForms(prev => {
        const next = new Map(prev);
        next.delete(formId);
        return next;
      });

      const message = error instanceof Error ? error.message : 'Failed to withdraw decline';
      setToast({
        show: true,
        message,
        type: 'error'
      });
    }
  };

  const handleWithdrawRevisions = async (formId: string) => {
    // Mark form as processing immediately
    setProcessingForms(prev => new Map(prev).set(formId, 'withdrawRevisions'));

    try {
      await formService.withdrawRevisions(formId);
      
      // Move from processing to fading state
      setProcessingForms(prev => {
        const next = new Map(prev);
        next.delete(formId);
        return next;
      });
      setFadingForms(prev => new Map(prev).set(formId, 'withdrawRevisions'));

      // Wait for fade animation to complete (~1 second)
      await new Promise(resolve => setTimeout(resolve, 1000));

      await Promise.all([
        fetchForms(),
        fetchFilterCounts()
      ]);

      // Clear fading state
      setFadingForms(prev => {
        const next = new Map(prev);
        next.delete(formId);
        return next;
      });

      setToast({
        show: true,
        message: 'Revisions withdrawn successfully',
        type: 'success'
      });
    } catch (error) {
      console.error('Error withdrawing revisions:', error);
      
      // Remove from processing state on error
      setProcessingForms(prev => {
        const next = new Map(prev);
        next.delete(formId);
        return next;
      });

      setToast({
        show: true,
        message: 'Failed to withdraw revisions',
        type: 'error'
      });
    }
  };

  const handleSaveField = async (formId: string, fieldId: number, value: string) => {
    try {
      setIsLoading(true);
      await formService.updateFormField(formId, fieldId, value);
      
      // Update the local state with the updated field (functional update to avoid stale closure)
      setForms(prevForms => prevForms.map(form => {
        if (form.id === formId) {
          return {
            ...form,
            fields: form.fields.map(field => {
              if (field.id === fieldId) {
                return { ...field, value };
              }
              return field;
            })
          };
        }
        return form;
      }));
      alert('Field updated successfully!');
    } catch (error) {
      console.error('Error updating field:', error);
      alert('Failed to update field');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFieldsReplace = (formId: string, fields: Form['fields']) => {
    setForms((prevForms) =>
      prevForms.map((form) => (form.id === formId ? { ...form, fields } : form)),
    );
  };

  const handleHeResubmitted = async (formId: string) => {
    setForms((prevForms) =>
      prevForms.map((form) =>
        form.id === formId ? { ...form, status: 'revisions completed' } : form,
      ),
    );
    updateSessionSubmissionStatus(formId, 'pending');
    await Promise.all([fetchForms(), fetchFilterCounts()]);
  };

  const handleApplyFilters = (filters: FormAdvancedFilters) => {
    console.log('Applying filters:', filters); // Debug log
    setAdvancedFilters(filters);
    // Reset to first page when applying filters
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleClearFilters = () => {
    setAdvancedFilters({ ...EMPTY_ADVANCED_FILTERS });
    // Reset to first page when clearing filters
    setPagination(prev => ({ ...prev, page: 1 }));
  };





  const waitingForServiceAreaOptions =
    advancedFilters.serviceAreaIds.length > 0 && filterOptions.serviceAreas.length === 0;

  // Any advanced (card) filter selected? All dropdown filters are multi-select, so these are lists.
  const hasActiveAdvancedFilters =
    advancedFilters.employeeNames.length > 0 ||
    advancedFilters.subcontractorNames.length > 0 ||
    advancedFilters.foremanAreaIds.length > 0 ||
    advancedFilters.serviceAreaIds.length > 0 ||
    Boolean(advancedFilters.startDate) ||
    Boolean(advancedFilters.endDate);

  // Names for the "Active Filters" badges (ids fall back to themselves if options aren't loaded yet)
  const selectedServiceAreaNames = advancedFilters.serviceAreaIds.map(
    (id) => filterOptions.serviceAreas.find((area) => area.id.toString() === id)?.name ?? id
  );
  const selectedForemanAreaNames = advancedFilters.foremanAreaIds.map(
    (id) => filterOptions.foremanAreas.find((area) => area.id.toString() === id)?.name ?? id
  );

  if (loading || waitingForServiceAreaOptions) {
    return <div className="tw-flex tw-justify-center tw-p-5">
      <Spinner>Loading...</Spinner>
    </div>;
  }

  if (error) {
    return <Alert variant="danger" className="tw-m-3" role="alert">
      {error}
    </Alert>;
  }

  return (
    <div className="tw-w-full">

      {/* When viewing a single timecard via deep link, show back link and only that form */}
      {formIdFromUrl && (
        <div className="tw-mb-3">
          <Button as={Link} to="/approvals" variant="outline-secondary" size="sm">
            <i className="bi bi-arrow-left tw-mr-2" aria-hidden />
            Back to all submissions
          </Button>
        </div>
      )}

      {/* FormFilters component - always show */}
      <FormFilters
        forms={forms}
        filterOptions={filterOptions}
        filters={advancedFilters}
        formType={formType}
        onClearFilters={handleClearFilters}
        onApplyFilters={handleApplyFilters}
      />

      {/* Search field appears above the status buttons when visible */}
      {isSearchVisible && (
        <Row className="tw-mb-3 search-container">
          <Col xs={12}>
            <InputGroup className="tw-shadow-sm">
              <InputGroup.Text className="tw-bg-primary tw-text-white tw-border-primary">
                <i className="bi bi-search"></i>
              </InputGroup.Text>
              <UiForm.Control
                type="text"
                className="tw-border-primary/40"
                placeholder="Search by Ticket #, Form Name, Employee, Subcontractor, Task ID, Task Name, Asset, Material, Vehicle, Comments, Rates, Quantities, Costs, Total, or any Summary table data..."
                value={searchInput}
                onChange={(e) => handleSearchInputChange(e.target.value)}
                onKeyDown={(e) => handleSearchKeyPress(e)}
                autoFocus
              />
              <Button
                variant="primary"
                type="button"
                onClick={handleSearch}
                title="Search"
              >
                <i className="bi bi-search"></i>
              </Button>
              {(searchInput || searchTerm) && (
                <Button
                  variant="outline-primary"
                  type="button"
                  onClick={clearSearch}
                  title="Clear search"
                >
                  <i className="bi bi-x-lg"></i>
                </Button>
              )}
            </InputGroup>
            {searchTerm && (
              <div className="tw-text-content-muted tw-text-sm tw-mt-1">
                Searching for: "{searchTerm}"
              </div>
            )}
          </Col>
        </Row>
      )}

      <div className="tw-mb-3">
        <div className="tw-flex tw-flex-col tw-gap-3 md:tw-hidden">
          <label className="tw-flex tw-flex-col tw-gap-1">
            <span className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-content-muted">Status</span>
            <select
              className="tw-w-full tw-min-h-[44px] tw-rounded tw-border tw-border-line-input tw-bg-surface tw-px-3 tw-py-2 tw-text-base tw-text-content"
              value={statusFilter}
              onChange={(e) => handleStatusFilterChange(e.target.value)}
              aria-label="Filter by status"
            >
              {STATUS_FILTER_BUTTONS.map(({ value: status, label }) => {
                const count = getFormCountByStatus(status);
                const optionLabel =
                  count > 0 && status !== 'all' && status !== 'approved' && status !== 'declined'
                    ? `${label} (${count})`
                    : label;
                return (
                  <option key={status} value={status}>{optionLabel}</option>
                );
              })}
            </select>
          </label>
          <label className="tw-flex tw-flex-col tw-gap-1">
            <span className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-content-muted">Form Type</span>
            <select
              className="tw-w-full tw-min-h-[44px] tw-rounded tw-border tw-border-line-input tw-bg-surface tw-px-3 tw-py-2 tw-text-base tw-text-content"
              value={formType}
              onChange={(e) => onFormTypeChange?.(e.target.value as ApprovalFormType)}
              aria-label="Filter by form type"
            >
              {APPROVAL_FORM_TYPES.map((type) => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="tw-hidden md:tw-flex md:tw-flex-row md:tw-justify-between md:tw-items-center tw-gap-3">
          <div className="tw-flex tw-flex-wrap tw-gap-2">
            {STATUS_FILTER_BUTTONS.map(({ value: status, label }) => {
              const count = getFormCountByStatus(status);
              const active = statusFilter === status;
              const showCount =
                count > 0 && status !== 'all' && status !== 'approved' && status !== 'declined';
              return (
                <Button
                  key={status}
                  type="button"
                  variant={active ? 'primary' : 'outline-primary'}
                  aria-pressed={active}
                  onClick={() => handleStatusFilterChange(status)}
                >
                  {label}
                  {showCount && (
                    <Badge bg="warning" text="dark" className="tw-ml-2">
                      {count}
                    </Badge>
                  )}
                </Button>
              );
            })}
          </div>
          <div className="tw-flex tw-flex-wrap tw-gap-2">
            {APPROVAL_FORM_TYPES.map((type) => (
              <Button
                key={type.value}
                type="button"
                variant={formType === type.value ? 'primary' : 'outline-primary'}
                aria-pressed={formType === type.value}
                onClick={() => onFormTypeChange?.(type.value)}
              >
                {type.label}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Show filter summary if any filters are active */}
      {hasActiveAdvancedFilters && (
        <Alert variant="light" className="tw-mb-3 tw-flex tw-items-center">
          <span className="tw-mr-2">
            <i className="bi bi-funnel-fill tw-text-content-secondary"></i>
          </span>
          <div className="tw-grow">
            <strong>Active Filters:</strong>
            {advancedFilters.serviceAreaIds.length > 0 && <Badge bg="info" text="dark" className="tw-ml-2">Service Area: {selectedServiceAreaNames.join(', ')}</Badge>}
            {advancedFilters.foremanAreaIds.length > 0 && <Badge bg="info" text="dark" className="tw-ml-2">Foreman Area: {selectedForemanAreaNames.join(', ')}</Badge>}
            {advancedFilters.employeeNames.length > 0 && <Badge bg="info" text="dark" className="tw-ml-2">Employee: {advancedFilters.employeeNames.join(', ')}</Badge>}
            {advancedFilters.subcontractorNames.length > 0 && <Badge bg="info" text="dark" className="tw-ml-2">Subcontractor: {advancedFilters.subcontractorNames.join(', ')}</Badge>}
            {advancedFilters.startDate && <Badge bg="info" text="dark" className="tw-ml-2">Work From: {advancedFilters.startDate}</Badge>}
            {advancedFilters.endDate && <Badge bg="info" text="dark" className="tw-ml-2">Work To: {advancedFilters.endDate}</Badge>}
          </div>
          <Button
            variant="outline-secondary"
            size="sm"
            className="tw-ml-auto"
            onClick={handleClearFilters}
          >
            Clear All
          </Button>
        </Alert>
      )}

      {/* Show no forms message if no forms found */}
      {forms.length === 0 ? (
        <Alert variant="info">
          <div className="tw-flex tw-items-center">
            <i className="bi bi-info-circle tw-mr-2"></i>
            <div>
              <strong>No forms found.</strong>
              {(hasActiveAdvancedFilters || searchTerm) && (
                <div className="tw-mt-1">
                  Try adjusting your filters or search criteria to see more results.
                </div>
              )}
            </div>
          </div>
        </Alert>
      ) : (
        <>
          <div className="forms-list forms-container">
            {forms.map(form => (
              <FormCard
                key={form.id}
                form={form}
                // handleApprove also carries `confirmDuplicate`, which is internal to the
                // duplicate-warning retry and not the card's business.
                onApprove={(formId, options) => handleApprove(formId, false, options)}
                onReject={handleReject}
                onDecline={handleDecline}
                onSaveField={handleSaveField}
                onFieldsReplace={handleFieldsReplace}
                onHeResubmitted={handleHeResubmitted}
                onWithdrawApproval={handleWithdrawApproval}
                onWithdrawRevisions={handleWithdrawRevisions}
                onWithdrawDecline={handleWithdrawDecline}
                availableTasks={availableTasks}
                tasksLoading={tasksLoading}
                persistedLabourEfforts={persistedLabourByFormId[String(form.id)] ?? []}
                labourOvertimeAllocations={labourOtAllocationsByFormId[String(form.id)] ?? []}
                onLabourOtAllocationsRefresh={() => refreshLabourAndLightTasksForForms(forms)}
                isProcessing={processingForms.has(form.id)}
                processingAction={processingForms.get(form.id) || null}
                isFading={fadingForms.has(form.id)}
                fadingAction={fadingForms.get(form.id) || null}
                defaultExpanded={expandFormId != null && String(form.id) === String(expandFormId)}
              />
            ))}
          </div>

          {/* Add pagination controls (hidden when viewing single form via formId deep link) */}
          {!formIdFromUrl && pagination.totalPages > 1 && (
            <div className="tw-flex tw-justify-center tw-mt-3 pagination-container">
              <ServerPagination
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                totalCount={pagination.totalCount}
                pageSize={pagination.limit}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                loading={loading}
                className="tw-mt-3"
              />
            </div>
          )}
        </>
      )}
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast(prev => ({ ...prev, show: false }))}
      />

      {/* Duplicate Form Warning Modal */}
      <DuplicateFormWarningModal
        isOpen={duplicateWarningModal.isOpen}
        onClose={handleDuplicateWarningCancel}
        onConfirm={handleDuplicateWarningConfirm}
        duplicateWarning={duplicateWarningModal.duplicateWarning}
      />

    </div>
  );
};

export default FormList;